import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import Classes from './Membership.module.css';
import { TickCircleIcon } from '../../../../../utils/Icons';
import { useNavigate } from 'react-router-dom';
import StoreSelectionPopup from '../../../../GlobalComp/Footer/StoreSelectionPopup';
import backend from '../../../../../api';

const IOS_APP_LINK = 'https://apps.apple.com/us/app/fuelclubs/id6753362541';
const ANDROID_APP_LINK = 'https://play.google.com/store/apps/details?id=com.devzona.fuelclubs';

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const cardIn = {
  hidden: { opacity: 0, y: 28, scale: 0.98, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0)',
    transition: { duration: 0.55, ease: 'easeOut' },
  },
};

const n = (v, fallback = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
};

const vehicleText = (limit) => {
  const lim = Number(limit);
  if (!Number.isFinite(lim)) return null;
  if (lim === 0) return 'No Vehicle Limit';
  return lim === 1 ? '1 Vehicle' : `${lim} Vehicles`;
};

export default function Membership() {
  const navigate = useNavigate();

  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'biannually' | 'annually'
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  const [showStorePopup, setShowStorePopup] = useState(false);

  const [dbPlans, setDbPlans] = useState(null); // null = loading
  const [loadingPlans, setLoadingPlans] = useState(false);

  const isMobile = width < 768;

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleGetStarted = () => {
    // if (isMobile) setShowStorePopup(true);
    // else 
      navigate('/signup');
  };

  const handleStoreSelect = (platform) => {
    const url = platform === 'ios' ? IOS_APP_LINK : ANDROID_APP_LINK;
    window.open(url, '_blank');
  };

  useEffect(() => {
    async function load() {
      setLoadingPlans(true);
      try {
        const { data } = await backend.get('/api/plan-config');
        const items = Array.isArray(data?.items) ? data.items : [];
        const order = ['tier1', 'tier2', 'tier3', 'fleet'];
        items.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
        setDbPlans(items);
      } catch (e) {
        console.error('Landing membership load plans error:', e);
        setDbPlans([]); // still render UI
      } finally {
        setLoadingPlans(false);
      }
    }
    load();
  }, []);

  const plans = useMemo(() => {
    if (dbPlans === null || loadingPlans) {
      return [
        { key: 'sk_tier1', __skeleton: true, highlight: false },
        { key: 'sk_tier2', __skeleton: true, highlight: true },
        { key: 'sk_tier3', __skeleton: true, highlight: false },
      ];
    }

    const byKey = new Map((dbPlans || []).map((p) => [p.key, p]));
    const t1 = byKey.get('tier1');
    const t2 = byKey.get('tier2');
    const t3 = byKey.get('tier3');

    // amounts
    const t1m = n(t1?.pricing?.monthly?.amount, 0);
    const t1b = n(t1?.pricing?.biannually?.amount, 0);
    const t1a = n(t1?.pricing?.annually?.amount, 0);

    const t2m = n(t2?.pricing?.monthly?.amount, 20);
    const t2b = n(t2?.pricing?.biannually?.amount, 108);
    const t2a = n(t2?.pricing?.annually?.amount, 220);

    const t3m = n(t3?.pricing?.monthly?.amount, 40);
    const t3b = n(t3?.pricing?.biannually?.amount, 216);
    const t3a = n(t3?.pricing?.annually?.amount, 440);

    // vehicle limits
    const t1VehicleFeature = vehicleText(t1?.vehiclesLimit) || 'No Vehicle Limit';
    const t2VehicleFeature = vehicleText(t2?.vehiclesLimit) || '1 Vehicle';
    const t3VehicleFeature = vehicleText(t3?.vehiclesLimit) || '2 Vehicles';

    return [
      {
        key: 'tier1',
        title: 'Tier 1',
        subtitle: 'Unlimited fill-ups, $9.99 fill-up fee',
        highlight: false,
        pricing: {
          monthly: { amount: String(t1m), unit: '/forever', billedNote: 'Free Forever', cta: 'Get started for free' },
          biannually: { amount: String(t1b), unit: '/forever', billedNote: 'Free Forever', cta: 'Get started for free' },
          annually: { amount: String(t1a), unit: '/forever', billedNote: 'Free Forever', cta: 'Get started for free' },
        },
        // ✅ vehicle limit from DB
        features: [t1VehicleFeature, 'Unlimited Fill-Ups', 'Fuel at Cost', 'No Set Up Fee'],
      },
      {
        key: 'tier2',
        title: 'Tier 2',
        subtitle: '1 vehicle and 4 fill-ups per month',
        highlight: true,
        pricing: {
          monthly: { amount: String(t2m), unit: '/month', billedNote: 'Billed Every 6 Months', cta: 'Start for free for first 1 month' },
          biannually: { amount: String(t2b), unit: '/6 months', billedNote: 'Billed Bi-Annually', cta: 'Get started for 10% off' },
          annually: { amount: String(t2a), unit: '/year', billedNote: 'Billed Annually', cta: 'Get started with one month free' },
        },
        // ✅ vehicle limit from DB
        features: [t2VehicleFeature, '4 Fill-Ups Per Month', 'Fuel at Cost', 'No Set Up Fee'],
      },
      {
        key: 'tier3',
        title: 'Tier 3',
        subtitle: '2 vehicles, 4 fill-ups per month per vehicle',
        highlight: false,
        pricing: {
          monthly: { amount: String(t3m), unit: '/month', billedNote: 'Billed Monthly', cta: 'Start for free for first 2 months' },
          biannually: { amount: String(t3b), unit: '/6 months', billedNote: 'Billed Bi-Annually', cta: 'Get started for 10% off' },
          annually: { amount: String(t3a), unit: '/year', billedNote: 'Billed Annually', cta: 'Get started with one month free' },
        },
        // ✅ vehicle limit from DB
        features: [t3VehicleFeature, '4 Fill-Ups Per Month Per Vehicle', 'Fuel at Cost', 'No Set Up Fee'],
      },
    ];
  }, [dbPlans, loadingPlans]);

  const getToggleBgLeft = () => {
    if (billing === 'monthly') return '4px';
    if (billing === 'biannually') return 'calc(33.333% + 4px)';
    return 'calc(66.666% + 4px)';
  };

  return (
    <>
      <motion.div id="membership" className={Classes.hero} whileInView="show">
        <motion.h1 className={Classes.subheading} variants={fadeUp}>
          Memberships
        </motion.h1>

        {/* Billing Toggle */}
        <motion.div className={Classes.toggleWrap} variants={fadeUp}>
          <div className={Classes.billingToggle}>
            <motion.div
              className={Classes.toggleBg}
              animate={{ left: getToggleBgLeft() }}
              transition={{ type: 'spring', stiffness: 550, damping: 32 }}
            />
            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === 'monthly' ? Classes.active : ''}`}
              onClick={() => setBilling('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === 'biannually' ? Classes.active : ''}`}
              onClick={() => setBilling('biannually')}
            >
              Bi-Annually
            </button>
            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === 'annually' ? Classes.active : ''}`}
              onClick={() => setBilling('annually')}
            >
              Annually
            </button>
          </div>
        </motion.div>

        {/* Cards */}
        <motion.div className={Classes.plansGrid} variants={stagger}>
          {plans.map((plan) => {
            if (plan.__skeleton) {
              return (
                <motion.div key={plan.key} className={Classes.card} variants={cardIn} style={{ opacity: 0.6, pointerEvents: 'none' }}>
                  <div className={`${Classes.cardTop} ${plan.highlight ? Classes.cardPremium : ''}`}>
                    <div className={Classes.cardHeader}>
                      <div className={Classes.cardTitle}>Tier</div>
                      <div className={Classes.cardSubtitle}>Loading...</div>
                    </div>
                    <div className={Classes.priceRow}>
                      <span className={Classes.price}>$—</span>
                      <span className={Classes.per}></span>
                    </div>
                    <div className={Classes.billedNote}>Loading...</div>
                    <button type="button" className={Classes.cta} disabled>
                      Loading...
                    </button>
                  </div>

                  <div className={Classes.cardBottom}>
                    <ul className={Classes.features}>
                      {[1, 2, 3, 4].map((i) => (
                        <li key={i} style={{ opacity: 0.7 }}>
                          <TickCircleIcon height="20" fill="var(--primary-color)" />
                          <h5>Loading...</h5>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            }

            const current = plan.pricing[billing];

            return (
              <motion.div
                key={plan.key}
                className={Classes.card}
                variants={cardIn}
                whileHover={{ y: -8, scale: 1.01 }}
                transition={{ type: 'spring', stiffness: 300, damping: 24 }}
              >
                <div className={`${Classes.cardTop} ${plan.highlight ? Classes.cardPremium : ''}`}>
                  <div className={Classes.cardHeader}>
                    <div className={Classes.cardTitle}>{plan.title}</div>
                    <div className={Classes.cardSubtitle}>{plan.subtitle}</div>
                  </div>

                  <div className={Classes.priceRow}>
                    <span className={Classes.price}>${current.amount}</span>
                    <span className={Classes.per}>{current.unit}</span>
                  </div>

                  <div className={Classes.billedNote}>{current.billedNote}</div>

                  <button type="button" className={Classes.cta} onClick={handleGetStarted}>
                    {current.cta}
                  </button>
                </div>

                <div className={Classes.cardBottom}>
                  <ul className={Classes.features}>
                    {plan.features.map((feature, i) => (
                      <li key={i}>
                        <TickCircleIcon height="20" fill="var(--primary-color)" />
                        <h5>{feature}</h5>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.h5 className={Classes.para} variants={stagger}>
          Have a fleet and need mobile gas delivery?{' '}
          <span onClick={() => navigate('/signup')}>Click here to get started</span>
        </motion.h5>
      </motion.div>

      <StoreSelectionPopup open={showStorePopup} onClose={() => setShowStorePopup(false)} onSelect={handleStoreSelect} />
    </>
  );
}
